import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripDotsVertical } from '@fortawesome/pro-light-svg-icons/faGripDotsVertical';
import { SerializedSocialLink, ArtistProfileLinkSource } from 'fym-shared/src/smartlinks/types';
import { LinkType } from 'fym-shared/src/smartlinks/linkTypes';
import { linkHost } from 'fym-shared/src/smartlinks/linkLabel';
import { TypedMemo } from '../../../common/types';
import { iconForLink } from '../../socialLinksList/utils';
import { SMLSwitch } from '../controls/SMLSwitch';
import { linkTypeName } from '../blocks/linkTypeName';
import { formMessages } from './formMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for every block form */
import styles from './SMLBlockForm.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialProfileRowProps {
  link: SerializedSocialLink;
  index: number;
  isOn: boolean;
  onToggle: (type: LinkType, on: boolean) => void;
  onDragStart: (index: number) => void;
  onDragOver: (index: number, event: React.DragEvent) => void;
  onDrop: () => void;
  onKeyMove: (index: number, delta: number) => void;
}

/** One profile of the Social links block (design d6): the handle, the name, the source and the switch. */
const SocialProfileRowComponent: React.FC<SocialProfileRowProps> = ({
  link,
  index,
  isOn,
  onToggle,
  onDragStart,
  onDragOver,
  onDrop,
  onKeyMove,
}) => {
  const intl = useIntl();
  const name = linkTypeName(link.type) ?? linkHost(link.url) ?? link.type;
  const toggle = useCallback((on: boolean) => onToggle(link.type, on), [link.type, onToggle]);
  const start = useCallback(() => onDragStart(index), [index, onDragStart]);
  const over = useCallback((event: React.DragEvent) => onDragOver(index, event), [index, onDragOver]);
  const drop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      onDrop();
    },
    [onDrop]
  );
  // Keyboard: the arrow keys on the handle move the profile one place.
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        onKeyMove(index, event.key === 'ArrowUp' ? -1 : 1);
      }
    },
    [index, onKeyMove]
  );
  const source = intl.formatMessage(
    link.source === ArtistProfileLinkSource.socialChannel
      ? formMessages.socialFromChannel
      : formMessages.socialFromConnection
  );

  return (
    <div className={clsx(styles.optionRow, !isOn && styles.optionRowOff)} onDragOver={over} onDrop={drop}>
      <button
        type="button"
        className={styles.linkButton}
        draggable
        onDragStart={start}
        onKeyDown={onKeyDown}
        aria-label={name}
      >
        <FontAwesomeIcon icon={faGripDotsVertical} />
      </button>
      <FontAwesomeIcon icon={iconForLink(link.type)} />
      <span className={styles.optionText}>
        {name}
        <span className={styles.optionSub}>
          {linkHost(link.url) ?? link.url} · {source}
        </span>
      </span>
      <SMLSwitch
        checked={isOn}
        onChange={toggle}
        ariaLabel={intl.formatMessage(formMessages.socialShow, { name })}
        testId={`social-switch-${link.type}`}
      />
    </div>
  );
};

export const SocialProfileRow = TypedMemo(SocialProfileRowComponent);
